import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Modal, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faClock,
  faUserClock,
  faComments,
  faEnvelopeOpenText,
  faArrowUpRightFromSquare,
  faCircleCheck,
  faCircleXmark,
  faPenToSquare,
  faTrashCan,
  faPhone,
  faEnvelope,
  faChartLine,
  faUserPlus,
  faStethoscope,
  faMedkit,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import {
  getAppointments,
  updateAppointmentStatus,
  deleteAppointment
} from '../../services/firebase/appointmentService';
import { getAllNotificationCounts } from '../../services/firebase/notificationService';

const STATUS_META = {
  pending: { label: 'Pending', cls: 'pending' },
  confirmed: { label: 'Confirmed', cls: 'confirmed' },
  completed: { label: 'Completed', cls: 'completed' },
  cancelled: { label: 'Cancelled', cls: 'cancelled' }
};

const renderStatus = (status) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return <span className={`admin-status-pill admin-status-pill--${meta.cls}`}>{meta.label}</span>;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - now.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return d >= start && d < end;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [notifications, setNotifications] = useState({
    pendingAppointments: 0,
    unreadMessages: 0,
    pendingTestimonials: 0,
    total: 0
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true';
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    loadAll();
  }, [navigate]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [appts, counts] = await Promise.all([
        getAppointments(),
        getAllNotificationCounts()
      ]);
      setAppointments(appts);
      setNotifications(counts);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = (appointment) => {
    setCurrentAppointment(appointment);
    setStatusUpdate(appointment.status || 'pending');
    setShowModal(true);
  };

  const saveStatusUpdate = async () => {
    try {
      await updateAppointmentStatus(currentAppointment.id, statusUpdate);
      setAppointments((prev) =>
        prev.map((a) => (a.id === currentAppointment.id ? { ...a, status: statusUpdate } : a))
      );
      try {
        const refreshed = await getAllNotificationCounts();
        setNotifications(refreshed);
      } catch (e) {}
      setShowModal(false);
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await deleteAppointment(id);
        setAppointments((prev) => prev.filter((a) => a.id !== id));
        try {
          const refreshed = await getAllNotificationCounts();
          setNotifications(refreshed);
        } catch (e) {}
      } catch (error) {
        console.error('Error deleting appointment:', error);
      }
    }
  };

  const stats = useMemo(() => {
    const total = appointments.length;
    const todayCount = appointments.filter((a) => isToday(a.date)).length;
    const weekCount = appointments.filter((a) => isThisWeek(a.date)).length;
    const pendingCount = appointments.filter((a) => (a.status || 'pending') === 'pending').length;
    const confirmedCount = appointments.filter((a) => a.status === 'confirmed').length;
    return { total, todayCount, weekCount, pendingCount, confirmedCount };
  }, [appointments]);

  const recentAppointments = useMemo(() => {
    return [...appointments]
      .sort((a, b) => {
        const ad = new Date(a.date || 0).getTime();
        const bd = new Date(b.date || 0).getTime();
        return bd - ad;
      })
      .slice(0, 6);
  }, [appointments]);

  const statCards = [
    {
      key: 'total',
      title: 'Total Appointments',
      value: stats.total,
      icon: faCalendarCheck,
      tone: 'sage',
      hint: stats.confirmedCount > 0 ? `${stats.confirmedCount} confirmed` : 'All time'
    },
    {
      key: 'today',
      title: "Today's Schedule",
      value: stats.todayCount,
      icon: faClock,
      tone: 'gold',
      hint: stats.todayCount === 0 ? 'No bookings today' : 'Bookings today'
    },
    {
      key: 'week',
      title: 'This Week',
      value: stats.weekCount,
      icon: faChartLine,
      tone: 'teal',
      hint: 'Upcoming this week'
    },
    {
      key: 'pending',
      title: 'Pending Review',
      value: stats.pendingCount,
      icon: faUserClock,
      tone: 'amber',
      hint: stats.pendingCount > 0 ? 'Needs your attention' : 'All caught up'
    }
  ];

  const engagementCards = [
    {
      key: 'messages',
      title: 'Unread Messages',
      value: notifications.unreadMessages || 0,
      icon: faEnvelopeOpenText,
      to: '/admin/messages',
      cta: 'Open inbox'
    },
    {
      key: 'testimonials',
      title: 'Testimonials Pending',
      value: notifications.pendingTestimonials || 0,
      icon: faComments,
      to: '/admin/testimonials',
      cta: 'Review now'
    }
  ];

  const quickActions = [
    { to: '/admin/appointments', icon: faCalendarCheck, label: 'Manage Appointments' },
    { to: '/admin/doctors', icon: faUserPlus, label: 'Add Doctor' },
    { to: '/admin/services', icon: faMedkit, label: 'Edit Services' },
    { to: '/admin/treatments', icon: faStethoscope, label: 'Edit Treatments' },
    { to: '/admin/settings', icon: faCog, label: 'Settings' }
  ];

  return (
    <div className="admin-dashboard">
      <header className="admin-page-head">
        <div>
          <span className="admin-page-eyebrow">Overview</span>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">A snapshot of bookings, messages, and engagement across your practice.</p>
        </div>
        <div className="admin-page-actions">
          <Button as={Link} to="/admin/appointments" className="admin-btn admin-btn-primary">
            <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
            View All Appointments
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="admin-loading-state">
          <Spinner animation="border" role="status" />
          <span>Loading dashboard…</span>
        </div>
      ) : (
        <>
          <Row className="g-4 admin-stat-row">
            {statCards.map((card) => (
              <Col xs={12} sm={6} xl={3} key={card.key}>
                <div className={`admin-stat-card admin-stat-card--${card.tone}`}>
                  <div className="admin-stat-icon">
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <div className="admin-stat-body">
                    <span className="admin-stat-title">{card.title}</span>
                    <span className="admin-stat-value">{card.value}</span>
                    <span className="admin-stat-hint">{card.hint}</span>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="g-4 mt-1">
            <Col lg={7} xl={8}>
              <section className="admin-panel">
                <header className="admin-panel-head">
                  <div>
                    <span className="admin-panel-eyebrow">Latest activity</span>
                    <h3 className="admin-panel-title">Recent Appointments</h3>
                  </div>
                  <Link to="/admin/appointments" className="admin-link-arrow">
                    View all <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1" />
                  </Link>
                </header>

                {recentAppointments.length === 0 ? (
                  <div className="admin-empty-state">
                    <div className="admin-empty-icon">
                      <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                    <h4>No appointments yet</h4>
                    <p>Bookings made through the website will appear here.</p>
                  </div>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Date &amp; Time</th>
                          <th>Treatment</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentAppointments.map((a) => (
                          <tr key={a.id}>
                            <td>
                              <div className="admin-patient-cell">
                                <div className="admin-patient-avatar">
                                  {(a.name || 'NA').charAt(0).toUpperCase()}
                                </div>
                                <div className="admin-patient-info">
                                  <span className="admin-patient-name">{a.name || 'Unknown'}</span>
                                  <span className="admin-patient-contact">
                                    <FontAwesomeIcon icon={faPhone} className="me-1" />
                                    {a.phone || '—'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="admin-date-cell">
                                <span className="admin-date-day">{formatDate(a.date)}</span>
                                <span className="admin-date-time">{a.time || '—'}</span>
                              </div>
                            </td>
                            <td>
                              <span className="admin-treatment-cell">
                                {a.treatment && a.treatment.length < 40 ? a.treatment : 'Consultation'}
                              </span>
                            </td>
                            <td>{renderStatus(a.status || 'pending')}</td>
                            <td className="text-end">
                              <div className="admin-row-actions">
                                <button
                                  type="button"
                                  className="admin-icon-btn"
                                  title="Update status"
                                  onClick={() => handleStatusUpdate(a)}
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                                <button
                                  type="button"
                                  className="admin-icon-btn admin-icon-btn--danger"
                                  title="Delete"
                                  onClick={() => handleDeleteAppointment(a.id)}
                                >
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </Col>

            <Col lg={5} xl={4}>
              <div className="admin-side-stack">
                <section className="admin-panel admin-panel--accent">
                  <header className="admin-panel-head">
                    <div>
                      <span className="admin-panel-eyebrow">Engagement</span>
                      <h3 className="admin-panel-title">Patient Communication</h3>
                    </div>
                  </header>
                  <div className="admin-engagement-stack">
                    {engagementCards.map((card) => (
                      <Link key={card.key} to={card.to} className="admin-engagement-card">
                        <div className="admin-engagement-icon">
                          <FontAwesomeIcon icon={card.icon} />
                        </div>
                        <div className="admin-engagement-body">
                          <span className="admin-engagement-title">{card.title}</span>
                          <span className="admin-engagement-value">{card.value}</span>
                        </div>
                        <span className="admin-engagement-cta">
                          {card.cta} <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>

                <section className="admin-panel">
                  <header className="admin-panel-head">
                    <div>
                      <span className="admin-panel-eyebrow">Shortcuts</span>
                      <h3 className="admin-panel-title">Quick Actions</h3>
                    </div>
                  </header>
                  <div className="admin-quick-grid">
                    {quickActions.map((qa) => (
                      <Link key={qa.to} to={qa.to} className="admin-quick-tile">
                        <FontAwesomeIcon icon={qa.icon} className="admin-quick-icon" />
                        <span>{qa.label}</span>
                      </Link>
                    ))}
                  </div>
                </section>
              </div>
            </Col>
          </Row>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Appointment Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentAppointment && (
            <>
              <div className="admin-modal-summary">
                <div>
                  <span className="admin-modal-label">Patient</span>
                  <span className="admin-modal-value">{currentAppointment.name}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Date</span>
                  <span className="admin-modal-value">{formatDate(currentAppointment.date)} · {currentAppointment.time}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Treatment</span>
                  <span className="admin-modal-value">{currentAppointment.treatment || '—'}</span>
                </div>
                <div>
                  <span className="admin-modal-label">Contact</span>
                  <span className="admin-modal-value">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />{currentAppointment.email || '—'}
                  </span>
                </div>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            <FontAwesomeIcon icon={faCircleXmark} className="me-2" />Cancel
          </Button>
          <Button className="admin-btn admin-btn-primary" onClick={saveStatusUpdate}>
            <FontAwesomeIcon icon={faCircleCheck} className="me-2" />Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
